/**
 * Print Nobleman chapter — Canva page-13 “Who Can Help”.
 * Activation trigger, portrait-forward cards (hero fills card top),
 * Limit These rows. Empty-state when nobleman data is missing.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  Briefcase,
  Crown,
  GraduationCap,
  Heart,
  Landmark,
  Megaphone,
  Sparkles,
  Sun,
  Swords,
  User,
  Users,
} from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import {
  getNoblemanImageForProfileType,
  resolveNoblemanTypeFromLabel,
} from "../../../../constants/noblemanProfiles";
import type { NoblemanType } from "../../../../types/nobleman";
import ZodiacIcons from "../../../zwds/icons";
import { PdfChapterShell, PdfPill } from "../atoms";
import {
  clipPdfText,
  noblemanCueLine,
  noblemanTagPills,
  stripPrintDashes,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  displayFontFamily,
  mutedGray,
  paleTan,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF Nobleman chapter.
 */
export interface PdfChapterNoblemanProps {
  /** Full monthly consultation content bundle. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Soft cap for a Limit These body line. */
const LIMIT_CLIP = 120;

/**
 * Thin gold rule with a diamond at the right end.
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Resolve English zodiac name to a shared animal icon component.
 *
 * @param zodiac - Zodiac display name
 * @returns Icon component or null
 */
function zodiacIconForName(
  zodiac: string
): React.FC<React.SVGProps<SVGSVGElement>> | null {
  const key = typeof zodiac === "string" ? zodiac.trim().toLowerCase() : "";
  if (key === "rat") {
    return ZodiacIcons.rat;
  }
  if (key === "ox") {
    return ZodiacIcons.ox;
  }
  if (key === "tiger") {
    return ZodiacIcons.tiger;
  }
  if (key === "rabbit") {
    return ZodiacIcons.rabbit;
  }
  if (key === "dragon") {
    return ZodiacIcons.dragon;
  }
  if (key === "snake") {
    return ZodiacIcons.snake;
  }
  if (key === "horse") {
    return ZodiacIcons.horse;
  }
  if (key === "goat" || key === "sheep") {
    return ZodiacIcons.goat;
  }
  if (key === "monkey") {
    return ZodiacIcons.monkey;
  }
  if (key === "rooster") {
    return ZodiacIcons.rooster;
  }
  if (key === "dog") {
    return ZodiacIcons.dog;
  }
  if (key === "pig") {
    return ZodiacIcons.pig;
  }
  return null;
}

/**
 * Lucide icon for a nobleman profile type. Crown / swords sit under the
 * product portrait on the Canva authority and bold cards.
 *
 * @param type - Resolved nobleman type key
 * @returns Lucide icon
 */
function noblemanIcon(type: NoblemanType): LucideIcon {
  if (type === "authority_high_status") {
    return Crown;
  }
  if (type === "bold_aggressive") {
    return Swords;
  }
  if (type === "older_female") {
    return Heart;
  }
  if (type === "male") {
    return Sun;
  }
  if (type === "stable_resource") {
    return Landmark;
  }
  if (type === "younger_junior") {
    return Sparkles;
  }
  if (type === "same_generation") {
    return Users;
  }
  if (type === "practical_leader") {
    return Briefcase;
  }
  if (type === "charismatic_expressive") {
    return Megaphone;
  }
  return GraduationCap;
}

/**
 * Format example years for the activation trigger card.
 *
 * @param years - Birth-year examples from the bundle
 * @returns Comma-separated years, or empty
 */
function yearExamplesLabel(years: readonly number[]): string {
  if (!Array.isArray(years) || years.length === 0) {
    return "";
  }
  const parts: string[] = [];
  for (const year of years) {
    if (typeof year === "number" && Number.isFinite(year)) {
      parts.push(String(Math.trunc(year)));
    }
  }
  return parts.join(", ");
}

/**
 * Allies chapter with zodiac trigger, vertical nobleman cards, and limits.
 */
export const PdfChapterNobleman: React.FC<PdfChapterNoblemanProps> = ({
  bundle,
  pageNumber,
}) => {
  const data = bundle.nobleman;
  const cautionList = Array.isArray(bundle.cautionList)
    ? bundle.cautionList.slice(0, 4)
    : [];
  const ZodiacIcon = data !== null ? zodiacIconForName(data.zodiac) : null;
  const profiles =
    data !== null && Array.isArray(data.matchedProfiles)
      ? data.matchedProfiles.slice(0, 2)
      : [];
  const cue =
    data !== null
      ? noblemanCueLine(
          typeof data.palaceName === "string" ? data.palaceName : "",
          typeof data.zodiac === "string" ? data.zodiac : ""
        )
      : "";
  const yearsLabel =
    data !== null ? yearExamplesLabel(data.yearExamples) : "";

  return (
    <PdfChapterShell
      chapterId="nobleman"
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore="always"
      fillPage={true}
      compact
      pageNumber={pageNumber}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
          fontFamily: bodyFontFamily,
          flexShrink: 0,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: paleTan,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <User size={20} color={terracotta} strokeWidth={1.8} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                + ALLIES
              </p>
              <h2
                style={{
                  margin: "4px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  color: charcoal,
                }}
              >
                Who Can Help
              </h2>
              {cue.length > 0 ? (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontFamily: displayFontFamily,
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: charcoal,
                  }}
                >
                  {stripPrintDashes(cue)}
                </p>
              ) : null}
            </div>
          </div>
          <PdfDiamondRule color={tanGold} />
        </div>

        {data === null ? (
          <p
            style={{
              margin: 0,
              fontFamily: bodyFontFamily,
              fontSize: 13,
              color: mutedGray,
            }}
          >
            Ally details are not available for this focus area.
          </p>
        ) : (
          <>
            <div
              className="mc-pdf-card"
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                background: cardWhite,
                border: `1px solid ${border}`,
                borderRadius: 14,
                padding: "12px 14px",
                fontFamily: bodyFontFamily,
                flexShrink: 0,
              }}
            >
              {ZodiacIcon !== null ? (
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: paleTan,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ZodiacIcon width={28} height={28} aria-hidden />
                </div>
              ) : null}
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontFamily: displayFontFamily,
                      fontSize: 18,
                      fontWeight: 700,
                      color: charcoal,
                    }}
                  >
                    {data.zodiac}
                  </p>
                  <PdfPill
                    label="Activation trigger"
                    color={cardWhite}
                    bg={terracotta}
                  />
                </div>
                {yearsLabel.length > 0 ? (
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: 12,
                      color: mutedGray,
                      lineHeight: 1.35,
                    }}
                  >
                    {`Example years: ${yearsLabel}`}
                  </p>
                ) : null}
              </div>
            </div>

            {profiles.length > 0 ? (
              <div
                className="mc-pdf-nobleman-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: profiles.length === 1 ? "1fr" : "1fr 1fr",
                  gap: 14,
                  flexShrink: 0,
                }}
              >
                {profiles.map((profile) => {
                  const typeLabel =
                    typeof profile.type === "string" ? profile.type.trim() : "";
                  const stars =
                    typeof profile.stars === "string" ? profile.stars.trim() : "";
                  const characteristics =
                    typeof profile.characteristics === "string"
                      ? profile.characteristics.trim()
                      : "";
                  const pills = noblemanTagPills(characteristics, stars);
                  const resolved = resolveNoblemanTypeFromLabel(typeLabel);
                  const Icon = noblemanIcon(resolved);
                  const portraitSrc =
                    typeLabel.length > 0
                      ? getNoblemanImageForProfileType(typeLabel)
                      : "";
                  return (
                    <article
                      key={typeLabel.length > 0 ? typeLabel : stars}
                      className="mc-pdf-card mc-pdf-nobleman-card"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        background: cardWhite,
                        border: `1px solid ${border}`,
                        borderRadius: 14,
                        fontFamily: bodyFontFamily,
                        overflow: "hidden",
                        padding: 0,
                      }}
                    >
                      {portraitSrc.length > 0 ? (
                        <div className="mc-pdf-nobleman-hero">
                          <img
                            src={portraitSrc}
                            alt={typeLabel}
                            className="mc-pdf-nobleman-portrait mc-pdf-nobleman-portrait--card"
                          />
                        </div>
                      ) : null}
                      <div className="mc-pdf-nobleman-body">
                        <span
                          aria-hidden="true"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 12,
                            background: paleTan,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={20} color={terracotta} strokeWidth={1.8} />
                        </span>
                        <p
                          style={{
                            margin: 0,
                            fontFamily: displayFontFamily,
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: charcoal,
                            lineHeight: 1.35,
                          }}
                        >
                          {typeLabel}
                        </p>
                        {pills.length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                            }}
                          >
                            {pills.map((pill) => (
                              <PdfPill
                                key={pill}
                                label={pill}
                                color={charcoal}
                                bg={paleTan}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: mutedGray,
                }}
              >
                No matched nobleman profiles for this palace this month.
              </p>
            )}

            <div style={{ flexShrink: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: terracotta,
                    flexShrink: 0,
                  }}
                >
                  Limit these
                </p>
                <PdfDiamondRule color={tanGold} />
              </div>
              {cautionList.length > 0 ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {cautionList.map((item) => {
                    const label =
                      typeof item.label === "string" ? item.label.trim() : "";
                    const why =
                      typeof item.why === "string" ? item.why.trim() : "";
                    if (label.length === 0) {
                      return null;
                    }
                    return (
                      <div
                        key={label}
                        className="mc-pdf-card"
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          background: cardWhite,
                          border: `1px solid ${border}`,
                          borderRadius: 12,
                          padding: "10px 12px",
                          fontFamily: bodyFontFamily,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 10,
                            background: paleTan,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AlertCircle size={16} color={terracotta} strokeWidth={2} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <p
                            style={{
                              margin: 0,
                              fontFamily: displayFontFamily,
                              fontSize: 12,
                              fontWeight: 700,
                              color: charcoal,
                              lineHeight: 1.3,
                            }}
                          >
                            {stripPrintDashes(label)}
                          </p>
                          {why.length > 0 ? (
                            <p
                              style={{
                                margin: "4px 0 0",
                                fontSize: 11,
                                color: mutedGray,
                                lineHeight: 1.45,
                              }}
                            >
                              {clipPdfText(stripPrintDashes(why), LIMIT_CLIP)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: mutedGray,
                  }}
                >
                  No limit-these patterns were recorded for this month.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterNobleman;
